/**
 * Utility functions for generating embed codes for trivia hosts
 *
 * Two embed modes:
 *   - Automatic: single <script> tag pointing to the public embed.js loader
 *   - Manual: plain <iframe> tag with all parameters baked in
 */

// ============================================================================
// SHARED CONSTANTS
// ============================================================================
const BASE_URL = 'https://atrivial.com';
const EMBED_LOADER_URL = `${BASE_URL}/embed.js?v=3`;
const EMBED_PATH = '/endless-trivia';
const SCHEDULED_QUIZ_EMBED_PATH = '/scheduled-quiz';
export const FULL_PLAYER_SANDBOX =
  'allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-downloads allow-forms';
export const FULL_VIEWPORT_EMBED_HEIGHT = '100svh';
export const STANDARD_EMBED_HEIGHT = '700px';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================
export interface EmbedCodeOptions {
  triviaType: 'dynamic' | 'general' | 'scheduled-quiz';
  registeredDomain: string;
  partnerId?: string;
  embedKey?: string;
  /** Optional tracking source; defaults to registeredDomain */
  source?: string;
  width?: string;
  height?: string;
  tag?: string;
  /** When set, generates a quiz-specific embed URL (/quiz/:slug) instead of the generic trivia path */
  quizSlug?: string;
  /** Base Atrivial app URL. Defaults to the production origin for copied embed code. */
  baseUrl?: string;
}

function assertScheduledQuizEmbedKey(options: EmbedCodeOptions): void {
  if (options.triviaType === 'scheduled-quiz' && !options.embedKey && !options.quizSlug) {
    throw new Error('scheduled-quiz embeds require an embedKey');
  }
}

export function buildEmbedUrl(options: EmbedCodeOptions): string {
  assertScheduledQuizEmbedKey(options);

  const {
    triviaType,
    registeredDomain,
    partnerId,
    embedKey,
    source,
    tag,
    quizSlug,
    baseUrl = BASE_URL,
  } = options;

  const sourceParam = source?.trim() || registeredDomain;
  const params = new URLSearchParams({
    view: 'iframe',
    origin: registeredDomain,
  });

  if (quizSlug) {
    params.set('source', registeredDomain);
    params.set('utm_source', registeredDomain);
  } else {
    if (sourceParam) {
      params.set('source', sourceParam);
      params.set('utm_source', sourceParam);
    } else if (registeredDomain) {
      params.set('utm_source', registeredDomain);
    }
    params.set('utm_medium', 'embed');
  }

  if (partnerId) {
    params.set('partner_id', partnerId);
  }

  if (!quizSlug && triviaType !== 'scheduled-quiz') {
    params.set('type', triviaType);
  }

  if (tag && triviaType === 'general') {
    params.set('tag', tag);
  }

  const embedPath = quizSlug
    ? `/quiz/${encodeURIComponent(quizSlug)}`
    : triviaType === 'scheduled-quiz'
      ? `${SCHEDULED_QUIZ_EMBED_PATH}/${encodeURIComponent(embedKey || '')}`
      : EMBED_PATH;
  return `${baseUrl.replace(/\/$/, '')}${embedPath}?${params.toString()}`;
}

/**
 * Generates a simple iframe embed code (universal compatibility)
 * Works everywhere: WordPress, Wix, Squarespace, Shopify, etc.
 * Note: For dynamic trivia, URL detection may be less reliable without the script wrapper
 * @param options - Configuration options for the embed code
 * @returns Formatted HTML string ready to copy and paste
 */
export function generateSimpleIframeCode(options: EmbedCodeOptions): string {
  const { width = '100%', height } = options;
  const iframeSrc = buildEmbedUrl(options);
  const resolvedHeight = height?.trim() || FULL_VIEWPORT_EMBED_HEIGHT;
  const heightStyle =
    resolvedHeight === FULL_VIEWPORT_EMBED_HEIGHT
      ? `height:${STANDARD_EMBED_HEIGHT};height:${FULL_VIEWPORT_EMBED_HEIGHT};`
      : `height:${resolvedHeight};`;

  const embedCode = `<!-- Atrivial Trivia Embed -->
<iframe
  id="atrivial-iframe"
  src="${iframeSrc}"
  style="width:${width};${heightStyle}border:0;display:block;"
  sandbox="${FULL_PLAYER_SANDBOX}"
  allow="web-share; clipboard-write"
  referrerpolicy="no-referrer-when-downgrade"
  title="Atrivial Interactive Trivia"
></iframe>`;

  return embedCode;
}

/**
 * Generates a script-tag embed code that loads the public embed.js loader.
 * Framework-agnostic: works in any HTML page, React SPA, Next.js, Vue, etc.
 * The loader script handles iframe creation, dynamic postMessage, and SPA detection.
 * @param options - Configuration options for the embed code
 * @returns Formatted HTML string ready to copy and paste
 */
export function generateAutomaticScriptCode(options: EmbedCodeOptions): string {
  assertScheduledQuizEmbedKey(options);

  const {
    triviaType,
    registeredDomain,
    partnerId,
    embedKey,
    source,
    width,
    height,
    tag,
    quizSlug,
  } = options;

  const attrs: string[] = [
    `src="${EMBED_LOADER_URL}"`,
    `data-type="${triviaType}"`,
    `data-origin="${registeredDomain}"`,
  ];
  const resolvedHeight = height?.trim() || FULL_VIEWPORT_EMBED_HEIGHT;

  const sourceParam = source?.trim();
  if (sourceParam && sourceParam !== registeredDomain) {
    attrs.push(`data-source="${sourceParam}"`);
  }

  if (partnerId) {
    attrs.push(`data-partner-id="${partnerId}"`);
  }

  if (embedKey) {
    attrs.push(`data-embed-key="${embedKey}"`);
  }

  if (tag) {
    attrs.push(`data-tag="${tag}"`);
  }

  if (quizSlug) {
    attrs.push(`data-quiz-slug="${quizSlug}"`);
  }

  if (width && width !== '100%') {
    attrs.push(`data-width="${width}"`);
  }

  if (resolvedHeight !== STANDARD_EMBED_HEIGHT) {
    attrs.push(`data-height="${resolvedHeight}"`);
  }

  return `<!-- Atrivial Trivia Embed (Automatic) -->
<script ${attrs.join('\n        ')}></script>`;
}

/**
 * Get platform-specific embed instructions
 */
export function getPlatformInstructions(
  platform: 'wordpress' | 'wix' | 'squarespace' | 'shopify' | 'general'
): string {
  const instructions = {
    wordpress: `**WordPress Instructions:**
1. Edit your page/post
2. Add a "Custom HTML" block (Gutenberg) or use the "Text" tab (Classic Editor)
3. Paste the script tag
4. Publish/Update your page`,

    wix: `**Wix Instructions:**
1. Edit your site in Wix Editor
2. Click "+ Add" and select "Embed"
3. Choose "Custom Embeds" → "Embed a Widget"
4. Paste the script tag
5. Adjust size and position as needed
6. Publish your site`,

    squarespace: `**Squarespace Instructions:**
1. Edit your page
2. Add a "Code" block
3. Paste the script tag
4. Save and publish`,

    shopify: `**Shopify Instructions:**
1. Go to Online Store → Pages (or Posts)
2. Edit the page where you want to add trivia
3. Click "Show HTML" button
4. Paste the script tag
5. Save`,

    general: `**General Instructions:**
1. Copy the embed code
2. Paste it into your website's HTML where you want the trivia to appear
3. Save and publish your page`,
  };

  return instructions[platform];
}

function copyWithExecCommand(text: string): boolean {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  const successful = document.execCommand('copy');
  textArea.remove();
  return successful;
}

/**
 * Copy text to clipboard using the modern Clipboard API with fallback
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'NotAllowedError')) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  }
  try {
    return copyWithExecCommand(text);
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
}

/**
 * Copy after async preparation while preserving the user-gesture chain (Safari/WebKit).
 * Call synchronously from a click handler; pass async work via getText.
 */
export async function copyToClipboardWhenReady(getText: () => Promise<string>): Promise<boolean> {
  if (
    typeof ClipboardItem !== 'undefined' &&
    navigator.clipboard?.write &&
    window.isSecureContext
  ) {
    try {
      const textPromise = getText();
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': textPromise.then((text) => new Blob([text], { type: 'text/plain' })),
        }),
      ]);
      return true;
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'NotAllowedError')) {
        console.warn('Deferred clipboard copy failed, falling back:', err);
      }
    }
  }

  const text = await getText();
  return copyToClipboard(text);
}

/**
 * Download text as a file
 */
export function downloadEmbedCodeAsFile(content: string, registeredDomain: string): void {
  const sanitizedDomain = registeredDomain.replace(/[^a-z0-9]/gi, '-').toLowerCase();
  const filename = `atrivial-embed-${sanitizedDomain}.html`;

  const blob = new Blob([content], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
